/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import LeadDetails from "./LeadDetails";
import Loading from "./Loading";
import fetchHelper from "../services/fetchHelper";
import { useContext } from "react";
import { Dashboard2Context } from "./dashboard2";

import "./MFRData.css";

export const Editable = ({
  type = "text",
  inputStyle,
  label,
  btnWidth,
  value,
  onSubmit = () => null,
  isLoading,
  isError,
  initialValue,
}) => {
  const [isHide, setIsHide] = useState(true);
  const [message, setMessage] = useState(type === "date" ? new Date(initialValue || "") : initialValue || "");
  const close = (e) => {
    e.preventDefault();
    setIsHide(true);
  };
  const show = (e) => {
    e.preventDefault();
    setIsHide(false);

    setMessage(value || message);
  };
  useEffect(() => {
    if (typeof isError === "boolean") {
      setIsHide(false);
    }
  }, [isError]);
  const isValidDate = (date) => {
    if (!date) return false;
    if (date.toString() === "Invalid Date") return false;
    return date && Object.prototype.toString.call(date) === "[object Date]" && !isNaN(date);
  };
  return (
    <>
      {isLoading ? (
        !isLoading
      ) : isHide ? (
        <>
          <span>{value}</span>
          <button id="rv-lead-btn" onClick={show} style={{ width: btnWidth }}>
            <span id="lv-lead-value" style={{ color: "#000", marginRight: 5 }}>
              {message
                ? isValidDate(message)
                  ? message.toLocaleDateString()
                  : message.toString().replace("Invalid Date", "")
                : null}
            </span>
            {!message ||
            message === "" ||
            (typeof message === "object" && Object.keys(message).length === 0) ? (
              <div className="icon-64 pencil-gray-icon" />
            ) : null}
          </button>
        </>
      ) : null}
      {label ? <div className="label">{label}</div> : null}
      <div className={["edit-content", isLoading ? !isLoading : isHide ? "hide" : null].join(" ")}>
        <form
          className="ib-fix"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(message);
            close(e);
          }}
          style={{ display: "flex", justifyContent: "spaceBetween" }}
        >
          <input
            id="lv-lead-input"
            className="ib-fix"
            type={type}
            step=".01"
            disabled={isLoading}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={{
              borderColor: isError ? "red" : undefined,
              ...(inputStyle || {}),
              marginRight: 5,
              textAlign: "center",
            }}
          />
          <button
            id="lv-lead-btn"
            className="btn btn-sm btn-success btn-submit"
            style={{ display: "flex", padding: "0px !important" }}
            disabled={isLoading}
          >
            {isLoading ? (
              <i
                id="results-loading-icon"
                className="saving-white-ico icon-64"
                style={{ height: 20, width: 20, marginTop: -4 }}
              />
            ) : (
              <i className="ti ti-save icon-64" style={{ height: 20, width: 20, marginTop: -4 }} />
            )}
          </button>
          <button
            id="lv-lead-cancel-btn"
            className="btn btn-sm btn-urgent btn-cancel"
            onClick={close}
            disabled={isLoading}
          >
            <i className="close-flat-ico icon-64" style={{ height: 20, width: 20, marginTop: -3 }}></i>
          </button>
        </form>
      </div>
    </>
  );
};

const MFREdit = ({ label, keyItem, btnWidth, type, inputStyle }) => {
  const { i } = useParams();
  const [isSuccess, setIsSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const { updateUserInfo, userInfo } = useContext(Dashboard2Context);

  useEffect(() => {
    if (isSuccess !== null) {
      setTimeout(() => {
        setIsSuccess(null);
      }, 3000);
    }
  }, [isSuccess]);

  const successIcoStyle = {
    height: !isSuccess ? 12 : 16,
    width: !isSuccess ? 16 : 14,
    marginLeft: !isSuccess ? 8 : 5,
    display: "block",
  };

  return (
    <>
      <Editable
        btnWidth={btnWidth}
        label={label}
        initialValue={userInfo[keyItem]}
        isLoading={isLoading}
        type={type}
        inputStyle={inputStyle}
        onSubmit={async (data) => {
          setIsLoading(true);
          try {
            await fetchHelper.put("/patientmanagement/rest/interrogations/" + i, { [keyItem]: data, id: i });
            updateUserInfo({ ...userInfo, [keyItem]: data });
            setIsSuccess(true);
          } catch (err) {
            setIsSuccess(false);
          }
          setIsLoading(false);
        }}
      />
      {!isLoading && isSuccess !== null ? (
        <div className={`${isSuccess ? "check-ico" : "close-ico"} icon-64`} style={successIcoStyle} />
      ) : null}
    </>
  );
};

const EditEpisode = ({ keyItem, keyData, type = "number", inputStyle, currentData }) => {
  const { i } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  useEffect(() => {
    if (isSuccess !== null) {
      setTimeout(() => {
        setIsSuccess(null);
      }, 3000);
    }
  }, [isSuccess]);
  return (
    <>
      <Editable
        initialValue={currentData ? currentData[keyItem] : null}
        type={type}
        isLoading={isLoading}
        inputStyle={inputStyle}
        onSubmit={async (data) => {
          let val = data;
          if (type === "date") val = new Date(val).getTime();
          else if (type === "number") val = parseFloat(val);

          setIsLoading(true);
          try {
            const body = {
              ...(currentData || {}),
              [keyItem]: val,
              eventName: keyData,
              interrogation: { id: i },
            };
            if (currentData) await fetchHelper.put("/patientmanagement/rest/events/" + currentData.id, body);
            else await fetchHelper.post("/patientmanagement/rest/events", body);
            setIsSuccess(true);
          } catch (err) {
            setIsSuccess(false);
          }
          setIsLoading(false);
        }}
      />
      {isSuccess !== null ? (
        <div
          className={`${isSuccess ? "check-ico" : "close-ico"} icon-64`}
          style={{ height: 15, width: 15, marginLeft: 5 }}
        />
      ) : null}
    </>
  );
};

const MFRData = () => {
  const { userInfo, mfrData } = useContext(Dashboard2Context);
  const [hideShow, setHideShow] = useState({
    leadDetails: true,
    batteryDetails: true,
    leadDetails2: true,
    eventDetails: true,
    deviceSettings: true,
    magnetMode: true,
    MRICompatibility: true,
  });

  const renderEdit = (key, label, btnWidth, type = "text", inputStyle) => {
    return (
      <MFREdit
        label={label}
        keyItem={key}
        userInfo={userInfo}
        btnWidth={btnWidth}
        type={type}
        inputStyle={inputStyle}
      />
    );
  };

  const renderEpisode = ({ key = [] }) => {
    const currentData = userInfo?.interrogationEvents?.filter((i) => i.eventName === key)[0];
    let title = "";
    switch (key) {
      case "mdtEventILRAF":
        title = "AF";
        break;
      case "mdtEventILRAsystole":
        title = "Pause/Asystole";
        break;
      case "mdtEventILRAT":
        title = "AT";
        break;
      case "mdtEventILRBrady":
        title = "Brady";
        break;
      case "mdtEventILRFVT":
        title = "FVT";
        break;
      case "mdtEventILRPatientInitiated":
        title = "Symptom";
        break;
      case "mdtEventILRTachyVT":
        title = "Tachy/VT";
        break;
    }
    return (
      <tr>
        <td className="row-title">{title}</td>
        <td className="col-edit">
          <EditEpisode
            keyItem="eventSinceLastReset"
            keyData={key}
            userInfo={userInfo}
            currentData={currentData}
          />
        </td>
        <td className="col-edit">
          <EditEpisode keyItem="eventLifetime" keyData={key} userInfo={userInfo} currentData={currentData} />
        </td>
        <td className="col-edit">
          <EditEpisode
            keyItem="eventCounterLastReset"
            type="date"
            keyData={key}
            inputStyle={{ width: 125, marginLeft: -50, padding: 3 }}
            userInfo={userInfo}
            currentData={currentData}
          />
        </td>
      </tr>
    );
  };

  const toggleHide = (key) => {
    console.log(key);
    setHideShow({ ...hideShow, [key]: !hideShow[key] });
  };

  const renderHideButton = (key) => {
    if (hideShow[key])
      return (
        <>
          <i className="ti ti-eye-slash"></i>
          <span>Hide</span>
        </>
      );
    else
      return (
        <>
          <i className="ti ti-eye"></i>
          <span>Show</span>
        </>
      );
  };

  const renderContent = () => {
    if (!mfrData) {
      return (
        <>
          <Loading />
          <Loading />
          <Loading />
          <Loading />
          <Loading />
        </>
      );
    }

    return (
      <>
        <div className="content-main-grp">
          <div className="interrogation-content">
            <LeadDetails
              id="lead-graph-details"
              userInfo={userInfo}
              onClickHide={(e) => {
                e.preventDefault();
                toggleHide("leadDetails");
              }}
              renderHideButton={renderHideButton("leadDetails")}
              visible={hideShow["leadDetails"]}
            />
            <div id="battery-details" className="tbl-set">
              <div className="grp-hdr">
                <button
                  className="btn btn-sm btn-hide-section"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleHide("batteryDetails");
                  }}
                >
                  {renderHideButton("batteryDetails")}
                </button>
                <h3>Battery Details</h3>
              </div>
              {hideShow["batteryDetails"] ? (
                <>
                  <div className="mfr-data-sb">
                    <table>
                      <tbody>
                        <tr>
                          <td className="row-li">
                            Battery Status:{" "}
                            <span style={{ marginLeft: 15 }}>{userInfo["batteryStatus"]}</span>{" "}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mfr-data-content mfr-data-tbl">
                    <table>
                      <tbody>
                        <tr>
                          <td className="row-title">Status</td>
                          <td className="col-edit">{renderEdit("batteryStatus")}</td>
                          <td></td>
                        </tr>
                        <tr>
                          <td className="row-title">Remaining Longevity</td>
                          <td className="col-edit">{renderEdit("remainingLongevity")}</td>
                          <td></td>
                        </tr>
                        <tr>
                          <td className="row-title">Measured Voltage</td>
                          <td className="col-edit">{renderEdit("measuredVoltage")}</td>
                          <td></td>
                        </tr>
                        <tr>
                          <td className="row-title">Minimum Voltage</td>
                          <td className="col-edit">{renderEdit("minimumVoltage")}</td>
                          <td></td>
                        </tr>

                        <tr>
                          <td className="row-title">Charge Time</td>
                          <td className="col-edit">{renderEdit("chargeTime")}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
              <div className="clearfix"></div>
            </div>

            <div id="lead-details" className="tbl-set">
              <div className="grp-hdr">
                <button
                  className="btn btn-sm btn-hide-section"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleHide("leadDetails2");
                  }}
                >
                  {renderHideButton("leadDetails2")}
                </button>
                <h3>Lead Details</h3>
              </div>
              {hideShow["leadDetails2"] ? (
                <>
                  <div className="mfr-data-sb">
                    <table>
                      <tbody>
                        <tr>
                          <td className="row-li">RA Lead:</td>
                          <td>
                            <span>{userInfo["raImpedance"]}</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="row-li">RV Lead:</td>
                          <td>
                            <span>{userInfo["rvImpedance"]}</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="row-li">LV Lead:</td>
                          <td>
                            <span>{userInfo["lvImpedance"]}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mfr-data-content mfr-data-tbl">
                    <table>
                      <thead>
                        <tr>
                          <th className="row-title">Lead Status</th>
                          <th>RA Lead</th>
                          <th>RV Lead</th>
                          <th>LV Lead</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="row-title">Sense</td>
                          <td className="col-edit">{renderEdit("raSense", "mV")}</td>
                          <td className="col-edit">{renderEdit("rvSense", "mV")}</td>
                          <td className="col-edit">{renderEdit("lvSense", "mV")}</td>
                        </tr>
                        <tr>
                          <td className="row-title">Impedance</td>
                          <td className="col-edit">{renderEdit("raImpedance", "Ohms")}</td>
                          <td className="col-edit">{renderEdit("rvImpedance", "Ohms")}</td>
                          <td className="col-edit">{renderEdit("lvImpedance", "Ohms")}</td>
                        </tr>
                        <tr>
                          <td className="row-title">Pacing Threshold Amplitude</td>
                          <td className="col-edit">{renderEdit("raPacingThresholdAmplitude", "V")}</td>
                          <td className="col-edit">{renderEdit("rvPacingThresholdAmplitude", "V")}</td>
                          <td className="col-edit">{renderEdit("lvPacingThresholdAmplitude", "V")}</td>
                        </tr>
                        <tr>
                          <td className="row-title">Pacing Threshold Pulse Width</td>
                          <td className="col-edit">{renderEdit("raPacingThresholdPulseWidth", "ms")}</td>
                          <td className="col-edit">{renderEdit("rvPacingThresholdPulseWidth", "ms")}</td>
                          <td className="col-edit">{renderEdit("lvPacingThresholdPulseWidth", "ms")}</td>
                        </tr>

                        <tr>
                          <td className="row-title">Date of Last Threshold Measurement</td>
                          <td className="col-edit">
                            {renderEdit("raLastThresholdMeasurementDate", undefined, 100, "date", {
                              width: "155px",
                            })}
                          </td>
                          <td className="col-edit">
                            {renderEdit("rvLastThresholdMeasurementDate", undefined, 100, "date", {
                              width: "155px",
                            })}
                          </td>
                          <td className="col-edit">
                            {renderEdit("lvLastThresholdMeasurementDate", undefined, 100, "date", {
                              width: "155px",
                            })}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <table>
                      <tbody>
                        <tr>
                          <td className="row-title">Shock Impedence (RV)</td>
                          <td className="col-edit">{renderEdit("shockImpedenceRV", "Ohms")}</td>
                          <td className="col-edit"></td>
                          <td className="col-edit"></td>
                        </tr>
                        <tr>
                          <td className="row-title">Shock Impedence (SVC)</td>
                          <td className="col-edit">{renderEdit("shockImpedenceSVC", "Ohms")}</td>
                          <td className="col-edit"></td>
                          <td className="col-edit"></td>
                        </tr>
                      </tbody>
                    </table>

                    <table>
                      <thead>
                        <tr>
                          <th>Lead Settings</th>
                          <th>RA Lead</th>
                          <th>RV Lead</th>
                          <th>LV Lead</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="row-title">Sensitivity</td>
                          <td className="col-edit">{renderEdit("raSensitivity", "mV")}</td>
                          <td className="col-edit">{renderEdit("rvSensitivity", "mV")}</td>
                          <td className="col-edit">{renderEdit("lvSensitivity", "mV")}</td>
                        </tr>
                        <tr>
                          <td className="row-title">Pacing Output Amplitude</td>
                          <td className="col-edit">{renderEdit("raPacingOutputAmplitude", "V")}</td>
                          <td className="col-edit">{renderEdit("rvPacingOutputAmplitude", "V")}</td>
                          <td className="col-edit">{renderEdit("lvPacingOutputAmplitude", "V")}</td>
                        </tr>

                        <tr>
                          <td className="row-title">Pacing Output Pulse Width</td>
                          <td className="col-edit">{renderEdit("raPacingOutputPulseWidth", "ms")}</td>
                          <td className="col-edit">{renderEdit("rvPacingOutputPulseWidth", "ms")}</td>
                          <td className="col-edit">{renderEdit("lvPacingOutputPulseWidth", "ms")}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
              <div className="clearfix"></div>
            </div>

            <div id="event-details" className="tbl-set">
              <div className="grp-hdr">
                <button
                  className="btn btn-sm btn-hide-section"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleHide("eventDetails");
                  }}
                >
                  {renderHideButton("eventDetails")}
                </button>
                <h3>Event Details</h3>
              </div>
              {hideShow["eventDetails"] ? (
                <>
                  <div className="mfr-data-sb">
                    <table>
                      <tbody>
                        <tr>
                          <td className="row-li">
                            CHA<sub>2</sub>DS<sub>2</sub>VAS<sub>c</sub>:
                          </td>
                          <td>
                            <span className="row-status">Not Available</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="row-li">HAS-BLED:</td>
                          <td>
                            <span className="row-status">Not Available</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mfr-data-content mfr-data-tbl">
                    <table>
                      <thead>
                        <tr>
                          <th>Therapy Counters</th>
                          <th>Since Last Reset</th>
                          <th>Episode Counter Last Cleared Date</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="row-title">Shocks Delivered</td>
                          <td className="col-edit">{renderEdit("shocksDeliverySinceLastReset")}</td>
                          <td className="col-edit">{renderEdit("shocksDeliveryECLClearDate")}</td>
                          <td className="col-edit"></td>
                        </tr>
                        <tr>
                          <td className="row-title">Shocks Aborted</td>
                          <td className="col-edit">{renderEdit("shocksAbortedSinceLastReset")}</td>
                          <td className="col-edit">{renderEdit("shocksAbortedECLClearDate")}</td>
                          <td className="col-edit"></td>
                        </tr>
                        <tr>
                          <td className="row-title">Anti-Tachy Pacing</td>
                          <td className="col-edit">{renderEdit("antiTachyPacingSinceLastReset")}</td>
                          <td className="col-edit">{renderEdit("antyTachyPacingECLClearDate")}</td>
                          <td className="col-edit"></td>
                        </tr>
                      </tbody>
                    </table>

                    <table>
                      <thead>
                        <tr>
                          <th>Episodes</th>
                          <th>Since Last Reset</th>
                          <th>Lifetime</th>
                          <th>Episode Counter Last Cleared Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderEpisode({ key: "mdtEventILRPatientInitiated" })}
                        {renderEpisode({ key: "mdtEventILRFVT" })}
                        {renderEpisode({ key: "mdtEventILRTachyVT" })}
                        {renderEpisode({ key: "mdtEventILRAsystole" })}
                        {renderEpisode({ key: "mdtEventILRBrady" })}
                        {renderEpisode({ key: "mdtEventILRAT" })}
                        {renderEpisode({ key: "mdtEventILRAF" })}
                      </tbody>
                    </table>

                    <table>
                      <thead>
                        <tr>
                          <th>Pacing Statistics</th>
                          <th>Since Last Reset</th>
                          <th>Episode Counter Last Cleared Date</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="row-title">Ap</td>
                          <td className="col-edit">{renderEdit("apSinceLastReset", "%")}</td>
                          <td className="col-edit">{renderEdit("apDeliveryECLClearDate")}</td>
                          <td className="col-edit"></td>
                        </tr>
                        <tr>
                          <td className="row-title">Vp</td>
                          <td className="col-edit">{renderEdit("vpSinceLastReset", "%")}</td>
                          <td className="col-edit">{renderEdit("vpAbortedECLClearDate")}</td>
                          <td className="col-edit"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
              <div className="clearfix"></div>
            </div>

            <div id="device-details" className="tbl-set">
              <div className="grp-hdr">
                <button
                  className="btn btn-sm btn-hide-section"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleHide("deviceSettings");
                  }}
                >
                  {renderHideButton("deviceSettings")}
                </button>
                <h3>Device Settings</h3>
              </div>
              {hideShow["deviceSettings"] ? (
                <>
                  <div className="mfr-data-sb">
                    <table>
                      <tbody>
                        <tr>
                          <td className="row-li">Pacing</td>
                          <td>
                            <span className="row-status"></span>
                          </td>
                        </tr>
                        <tr>
                          <td className="row-li">VT Therapy:</td>
                          <td>
                            <span className="row-status"></span>
                          </td>
                        </tr>
                        <tr>
                          <td className="row-li">Pacing Dependency:</td>
                          <td>
                            <span className="row-status">N/A</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="row-li">History of AFIB:</td>
                          <td>
                            <span className="row-status">N/A</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="row-li">History VT:</td>
                          <td>
                            <span className="row-status">N/A</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="row-li">History VF:</td>
                          <td>
                            <span className="row-status">N/A</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mfr-data-content mfr-data-tbl">
                    <table>
                      <thead>
                        <tr>
                          <th>Bradycardia Settings</th>
                          <th></th>
                          <th></th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="row-title">Pacing Mode</td>
                          <td className="col-edit">{renderEdit("bradyCardiaMode")}</td>
                          <td className="col-edit"></td>
                          <td className="col-edit"></td>
                        </tr>
                        <tr>
                          <td className="row-title">Lower Rate Limit</td>
                          <td className="col-edit">{renderEdit("bradyCardiaLowerRateLimit")}</td>
                          <td className="col-edit"></td>
                          <td className="col-edit"></td>
                        </tr>
                        <tr>
                          <td className="row-title">Max Tracking Rate</td>
                          <td className="col-edit">{renderEdit("bradyCardiaUpperRateLimit")}</td>
                          <td className="col-edit"></td>
                          <td className="col-edit"></td>
                        </tr>
                      </tbody>
                    </table>

                    <table>
                      <thead>
                        <tr>
                          <th>Tachycardia Settings</th>
                          <th></th>
                          <th></th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="row-title">VT Therapy</td>
                          <td className="col-edit">{renderEdit("vtTherapyStatus")}</td>
                          <td className="col-edit"></td>
                          <td className="col-edit"></td>
                        </tr>
                      </tbody>
                    </table>

                    <table>
                      <thead>
                        <tr>
                          <th>(Tachycardia Settings) Zone</th>
                          <th>Rate</th>
                          <th>ATP</th>
                          <th>Shocks</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="row-title">VT-1</td>
                          <td className="col-edit">{renderEdit("vt1Rate")}</td>
                          <td className="col-edit">{renderEdit("vt1Atp")}</td>
                          <td className="col-edit">{renderEdit("vt1Shocks")}</td>
                        </tr>
                        <tr>
                          <td className="row-title">VT/VT-2</td>
                          <td className="col-edit">{renderEdit("vt2Rate")}</td>
                          <td className="col-edit">{renderEdit("vt2Atp")}</td>
                          <td className="col-edit">{renderEdit("vt2Shocks")}</td>
                        </tr>
                        <tr>
                          <td className="row-title">VF</td>
                          <td className="col-edit">{renderEdit("vfRate")}</td>
                          <td className="col-edit">{renderEdit("vfAtp")}</td>
                          <td className="col-edit">{renderEdit("vfShocks")}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
              <div className="clearfix"></div>
            </div>

            <div id="event-details" className="tbl-set">
              <div className="grp-hdr">
                <button
                  className="btn btn-sm btn-hide-section"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleHide("magnetMode");
                  }}
                >
                  {renderHideButton("magnetMode")}
                </button>
                <h3>Magnet Mode</h3>
              </div>
              {hideShow["magnetMode"] ? (
                <>
                  <div className="mfr-data-sb">
                    <table>
                      <tbody>
                        <tr>
                          <td className="row-li">Mode</td>
                          <td>
                            <span className="row-status">Not Applicable</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mfr-data-content mfr-data-tbl">
                    <table>
                      <tbody>
                        <tr>
                          <td className="row-title">Magnet Mode</td>
                          <td className="col-edit">{renderEdit("magnetStatus")}</td>
                          <td className="col-edit"></td>
                          <td className="col-edit"></td>
                        </tr>
                        <tr>
                          <td className="row-title">Pacing</td>
                          <td className="row-title"></td>
                          <td className="col-edit"></td>
                          <td className="col-edit"></td>
                        </tr>
                        <tr>
                          <td className="row-title">Sensing</td>
                          <td className="row-title"></td>
                          <td className="col-edit"></td>
                          <td className="col-edit"></td>
                        </tr>
                        <tr>
                          <td className="row-title">Tachy</td>
                          <td className="row-title"></td>
                          <td className="col-edit"></td>
                          <td className="col-edit"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
              <div className="clearfix"></div>
            </div>

            <div id="event-details" className="tbl-set">
              <div className="grp-hdr">
                <button
                  className="btn btn-sm btn-hide-section"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleHide("MRICompatibility");
                  }}
                >
                  {renderHideButton("MRICompatibility")}
                </button>
                <h3>MRI Compatibility</h3>
              </div>
              {hideShow["MRICompatibility"] ? (
                <>
                  <div className="mfr-data-sb">
                    <table>
                      <tbody>
                        <tr>
                          <td className="row-li">Summary</td>
                          <td>
                            <span className="row-status">{userInfo["mriStatement"]}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mfr-data-content mfr-data-tbl">
                    <table>
                      <tbody>
                        <tr>
                          <td className="row-title">Statement</td>
                          <td className="col-edit">{renderEdit("mriStatement", null, "auto")}</td>
                          <td className="col-edit"></td>
                          <td className="col-edit"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
              <div className="clearfix"></div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <div id="mfr-data-container" className="tab-content mfr-data">
        <div className="mfr-main">
          {!userInfo ? (
            <>
              <Loading />
              <Loading />
              <Loading />
            </>
          ) : (
            renderContent()
          )}
        </div>
      </div>
    </>
  );
};

export default MFRData;
